import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Client } from '../../../context/ClientContext';
import { Badge, Card, Tabs } from '../../ui';
import { supabase } from '../../../lib/supabase';
import BacklinksInventory from './backlinks/BacklinksInventory';
import BacklinksOpportunities from './backlinks/BacklinksOpportunities';
import { formatRelative } from './backlinks/format';

interface BacklinksSectionProps {
  client: Client;
  expandedSection: string | null;
  toggleSection: (section: string) => void;
}

const TABS = { Inventory: 'inventory', Opportunities: 'opportunities' } as const;
type Tab = (typeof TABS)[keyof typeof TABS];

export default function BacklinksSection({ client }: BacklinksSectionProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncAt, setSyncAt] = useState<string | null>(null);

  // Active tab stored in URL (?backlinks_tab=inventory|opportunities)
  const tab: Tab = searchParams.get('backlinks_tab') === 'opportunities' ? 'opportunities' : 'inventory';
  const setTab = (label: string) => {
    const t = TABS[label as keyof typeof TABS];
    const next = new URLSearchParams(searchParams);
    if (t === 'inventory') next.delete('backlinks_tab');
    else next.set('backlinks_tab', t);
    // A detail panel belongs to the tab it was opened from.
    next.delete('backlink');
    next.delete('opportunity');
    setSearchParams(next);
  };

  const loadSyncStatus = useCallback(async () => {
    const { data, error } = await supabase
      .from('clients')
      .select('backlinks_sync_at, backlinks_sync_error')
      .eq('id', client.id)
      .single();
    if (error) {
      console.error('Error loading backlinks sync status:', error);
      return;
    }
    setSyncAt(data.backlinks_sync_at);
    setSyncError(data.backlinks_sync_error);
  }, [client.id]);

  useEffect(() => {
    loadSyncStatus();
  }, [loadSyncStatus]);

  return (
    <Card
      eyebrow="Backlinks"
      title="White-hat link building"
      actions={
        syncError ? (
          <Badge tone="down" dot title={syncError}>
            Sync failed
          </Badge>
        ) : (
          <span style={{ color: 'var(--text-faint)', fontSize: 'var(--text-sm)' }} title={syncAt ? new Date(syncAt).toLocaleString() : undefined}>
            {syncAt ? `Synced ${formatRelative(syncAt)}` : 'Never synced'}
          </span>
        )
      }
    >
      <Tabs
        tabs={Object.keys(TABS)}
        active={tab === 'inventory' ? 'Inventory' : 'Opportunities'}
        onChange={setTab}
        style={{ marginBottom: 'var(--space-5)' }}
      />

      {tab === 'inventory' ? <BacklinksInventory client={client} /> : <BacklinksOpportunities client={client} />}
    </Card>
  );
}
